import { useCallback, useEffect, useRef, useState } from "react";

const usePrevious = (value, initialValue) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useEffectDebugger = (
  effectHook,
  dependencies,
  dependencyNames = []
) => {
  const previousDeps = usePrevious(dependencies, []);

  const changedDeps = dependencies.reduce((accum, dependency, index) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency,
        },
      };
    }

    return accum;
  }, {});

  if (Object.keys(changedDeps).length) {
    console.log("[use-effect-debugger] ", changedDeps);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effectHook, dependencies);
};

export const useCallbackDebugger = (
  callbackHook,
  dependencies,
  dependencyNames = []
) => {
  const previousDeps = usePrevious(dependencies, []);

  const changedDeps = dependencies.reduce((accum, dependency, index) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency,
        },
      };
    }

    return accum;
  }, {});

  if (Object.keys(changedDeps).length) {
    console.log("[use-callback-debugger] ", changedDeps);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(callbackHook, dependencies);
};

export const useRetryRequest = (
  request,
  { validateResponse, maxTries = 0, retryOnErrors = false } = {}
) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const tryCount = useRef(1);
  const lastError = useRef(null);

  const shouldRetryOnError = useCallbackDebugger(
    (error) => {
      if (!retryOnErrors) return false;

      // If retryOnErrors is true, retry on any error
      if (retryOnErrors === true) return true;

      // If retryOnErrors is an array of status codes, check if the error status is in the array
      if (Array.isArray(retryOnErrors) && error?.response?.status) {
        return retryOnErrors.includes(error.response.status);
      }

      // If retryOnErrors is a function, call it with the error
      if (typeof retryOnErrors === "function") {
        return retryOnErrors(error);
      }

      return false;
    },
    [retryOnErrors]
  );

  const callRequest = useCallback(async () => {
    if (maxTries > 0 && tryCount.current > maxTries) {
      console.log(`Maximum retry attempts (${maxTries}) reached.`);
      return;
    }

    setIsRequesting(true);
    try {
      const response = await request();
      const isValid = validateResponse
        ? validateResponse(response)
        : !!response;
      if (isValid) {
        setRequestSuccess(true);
        lastError.current = null;
      } else {
        // If response is not valid, treat it as an error that might need retry
        const invalidResponseError = new Error("Invalid response received");
        invalidResponseError.invalidResponse = response;
        lastError.current = invalidResponseError;

        if (shouldRetryOnError(invalidResponseError)) {
          tryCount.current += 1;
          console.error(
            "Invalid response received, retrying...",
            invalidResponseError
          );
          // Don't automatically retry here - let the consumer decide when to retry
        } else {
          console.error(
            "Invalid response received, not retrying.",
            invalidResponseError
          );
        }
        setRequestSuccess(false);
      }
    } catch (error) {
      console.error("Request failed:", error);
      lastError.current = error;
      setRequestSuccess(false);

      if (shouldRetryOnError(error)) {
        tryCount.current += 1;
        console.log(
          `Error occurred, can retry. Attempt ${tryCount.current} of ${
            maxTries || "unlimited"
          }`
        );
        // Don't automatically retry here - let the consumer decide when to retry
      }
    } finally {
      setIsRequesting(false);
    }
  }, [request, validateResponse, maxTries, shouldRetryOnError]);

  useEffect(() => {
    if (!requestSuccess) {
      callRequest();
    }
  }, [requestSuccess, callRequest]);

  const retry = useCallback(() => {
    if (maxTries > 0 && tryCount.current > maxTries) {
      console.log(
        `Maximum retry attempts (${maxTries}) reached. Reset to try again.`
      );
      tryCount.current = 1;
    }

    setIsRequesting(false);
    setRequestSuccess(false);
    // This will trigger the useEffect to call callRequest again
  }, [maxTries]);

  return {
    isRequesting,
    requestSuccess,
    retry,
    error: lastError.current,
    attemptCount: tryCount.current,
  };
};
